import { HttpParams } from '@angular/common/http';

export function sortData<T>(data: T[], sortField: string, sortOrder: number): T[] {
	return data.sort((a: any, b: any) => {
		let value1 = a[sortField];
		let value2 = b[sortField];
		let result = 0;

		if (value1 == null && value2 != null) result = -1;
		else if (value1 != null && value2 == null) result = 1;
		else if (value1 == null && value2 == null) result = 0;
		else if (typeof value1 === 'string' && typeof value2 === 'string') result = value1.localeCompare(value2);
		else if (typeof value1 === 'number' && typeof value2 === 'number') {
			result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
		}

		return sortOrder * result;
	});
}

export function appendCriteria(params: HttpParams, criteria: { [key: string]: any }): HttpParams {
	Object.keys(criteria).forEach((key) => {
		const value = criteria[key];

		if (value !== null && value !== undefined && value !== '') {
			if (Array.isArray(value)) {
				// DOVE-2367 — multi-select filters. Emit `key[]=a&key[]=b` so PHP parses a
				// real array; params.set() would stringify to `key=a,b`, which Laravel reads
				// as the single string "a,b" and fails any `exists:`/`integer` rule.
				//
				// An empty array is not a filter, so it appends nothing at all.
				value
					.filter((v) => v !== null && v !== undefined && v !== '')
					.forEach((v) => {
						params = params.append(`${key}[]`, v);
					});
			} else if (typeof value === 'object') {
				// value is in a another form group
				params = appendCriteria(params, value); // recursively append nested objects
			} else {
				params = params.set(key, value);
			}
		}
	});

	return params;
}

export function convertToSnakeCase(obj: any): any {
	if (typeof obj !== 'object' || obj === null) {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map((v) => convertToSnakeCase(v));
	}

	return Object.keys(obj).reduce((result: any, key: string) => {
		const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase(); // item_detail_id => itemDetailId
		result[snakeKey] = convertToSnakeCase(obj[key]);
		return result;
	}, {});
}

/**
 * Recursively trims all string values in an object
 */
export function trimObjectValues(obj: any): any {
	if (obj === null || obj === undefined) {
		return obj;
	}

	if (typeof obj === 'string') {
		return obj.trim();
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => trimObjectValues(item));
	}

	if (typeof obj === 'object') {
		const trimmedObj: any = {};
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				trimmedObj[key] = trimObjectValues(obj[key]);
			}
		}
		return trimmedObj;
	}

	return obj;
}

/**
 * For time display.
 * Adjusts a date string to prevent timezone shifting issues in JavaScript.
 * (When creating a new date with new Date("1990-01-15"),
 * JavaScript interprets this as a UTC date at midnight. When this UTC date is then displayed
 * in your local timezone (which is behind UTC), it shows as the previous day.)
 */
export function adjustDateForTimezone(dateString: string): Date {
	if (!dateString) {
		throw new Error('Date string is required');
	}

	const date = new Date(dateString);
	return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

/**
 * Converts a Date object to YYYY/MM/DD format for backend submission
 */
export function toYearMonthDate(date: Date): string {
	if (!date) {
		throw new Error('Date is required');
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}/${month}/${day}`;
}

/**
 * For time submitted to the API
 * Converts a Date object to YYYY-MM-DD format for API submission
 */
export function toApiDateFormat(date: Date | string): string {
	if (!date) {
		throw new Error('Date is required');
	}

	// If it's already a string in the correct format, return as is
	if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date;
	}

	// Convert to Date object if it's not already
	const dateObj = date instanceof Date ? date : new Date(date);

	// Check for invalid date
	if (isNaN(dateObj.getTime())) {
		throw new Error('Invalid date provided');
	}

	const year = dateObj.getFullYear();
	const month = String(dateObj.getMonth() + 1).padStart(2, '0');
	const day = String(dateObj.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
}

/**
 * Formats a date string from API format (YYYY-MM-DD) to display format (MM/DD/YYYY).
 * This avoids timezone issues by not parsing the string into a Date object.
 */
export function formatDateStringForDisplay(dateString: string, fallback: string = '-'): string {
	if (!dateString) return fallback;
	const [year, month, day] = dateString.split('-');
	if (!year || !month || !day) return fallback;
	return `${month}/${day}/${year}`;
}

/**
 * Normalizes a value to a LOCAL calendar date at midnight (time stripped).
 *
 * Avoids the classic JS shift where `new Date('2026-07-15')` is parsed as UTC
 * midnight and rolls back a day in timezones behind UTC: a `YYYY-MM-DD`(...) string
 * is read by its calendar components, and a `Date` (e.g. from p-calendar) is
 * reduced to its local Y/M/D. Returns null for empty/invalid input.
 */
export function toLocalCalendarDate(value: Date | string | null | undefined): Date | null {
	if (!value) return null;

	if (value instanceof Date) {
		return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
	}

	// Read leading YYYY-MM-DD directly so a date-only string is never UTC-shifted.
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (match) {
		return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
	}

	const parsed = new Date(value);
	return isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Timezone-safe "is this calendar date before today?" check (DOVE-2155).
 * Compares local calendar days only, so it never suffers the UTC date-shift bug.
 */
export function isPastCalendarDate(value: Date | string | null | undefined): boolean {
	const selected = toLocalCalendarDate(value);
	if (!selected) return false;

	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return selected.getTime() < today.getTime();
}

/**
 * Formats an ISO/parseable date string as a localized date + time
 * (e.g. "07/06/2026, 03:21 PM"). Shared so the cleaning-log view modal and
 * the search results tooltip render label-print timestamps identically.
 */
export function formatDateTime(dateString: string | null | undefined): string {
	if (!dateString) return '';
	const date = new Date(dateString);
	return date.toLocaleString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
}

/**
 * Opens a PDF (or any) Blob in a new browser tab via an object URL.
 * Used for bearer-authed endpoints where a plain window.open(url) would not
 * carry the auth token. The object URL is revoked after a short delay so the
 * new tab has time to load it.
 */
export function openBlobInNewTab(blob: Blob, revokeDelayMs: number = 60000): void {
	const objectUrl = URL.createObjectURL(blob);
	window.open(objectUrl, '_blank');
	setTimeout(() => {
		URL.revokeObjectURL(objectUrl);
	}, revokeDelayMs);
}

/**
 * Order Type ids hidden from DBM dropdowns (DOVE-1933).
 * 4 = Service, 8 = Exchange. Interim removal; backend still returns them so
 * historical orders of these types continue to render in lists.
 */
export const HIDDEN_ORDER_TYPE_IDS: readonly number[] = [4, 8];

/**
 * DOVE-2110 — Build a `mailto:` link from a backend Send-Update payload and open
 * the user's default mail client. Nothing is sent by us; the staff member sends
 * from their own Outlook/Gmail.
 *
 * DOVE-2111 — `body` is optional and backward-compatible: Send-Update callers pass
 * only (to, subject) and are unaffected. When a body is provided (Lost Item Update),
 * it is appended as `&body=...` so the approved template is prefilled.
 */
export function openMailto(to: string[], subject: string, body?: string): void {
	const recipients = (to || []).join(',');
	const encodedSubject = encodeURIComponent(subject || '');
	let mailtoUrl = `mailto:${recipients}?subject=${encodedSubject}`;
	if (body) {
		mailtoUrl += `&body=${encodeURIComponent(body)}`;
	}

	// Use a real anchor click rather than assigning window.location.href:
	// the href assignment can be swallowed inside Angular's zone, and the
	// anchor approach is the most reliable cross-browser way to hand the
	// mailto: off to the OS default mail handler.
	const link = document.createElement('a');
	link.href = mailtoUrl;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export type StatusSeverity = 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast';

/**
 * DOVE-2341 — shared status → PrimeNG p-tag severity mapping so the status tag
 * reads consistently across every profile header. Case-insensitive; unknown
 * statuses fall back to a neutral 'secondary' rather than guessing a colour.
 *
 * The buckets mirror the Patient/Order mappings introduced in DOVE-2340:
 *   success  — a good / done / go state
 *   info     — a neutral in-flight state
 *   warning  — needs-attention / not-yet-actioned
 *   danger   — voided / cancelled / failed (destructive or error)
 *   secondary— inactive / draft / closed / unknown (neutral off)
 */
export function getStatusSeverity(status: string | null | undefined): StatusSeverity {
	switch ((status ?? '').trim().toLowerCase()) {
		case 'active':
		case 'completed':
		case 'confirmed':
		case 'scheduled':
		case 'ready':
		case 'approved':
		case 'shipped':
		case 'delivered':
		case 'verified':
			return 'success';
		case 'received':
		case 'viewed':
		case 'open':
		case 'new':
		case 'in progress':
		case 'dispatched':
			return 'info';
		case 'pending':
		case 'on hold':
		case 'unreceived':
		case 'partially received':
		case 'in review':
		case 'started':
			return 'warning';
		case 'voided':
		case 'cancelled':
		case 'canceled':
		case 'unrepairable':
		case 'failed':
		case 'lost':
		case 'rejected':
		case 'expired':
		case 'not ready':
			return 'danger';
		default:
			// inactive / discharged / draft / closed / anything unmapped
			return 'secondary';
	}
}

/**
 * "First Last" for display, skipping missing parts (DOVE-2586). A hospice house has a single
 * "Hospice Name" stored as the last name with no first name, so plain interpolation would
 * print "null Sunrise Hospice" or a stray space.
 */
export function patientDisplayName(firstName: string | null | undefined, lastName: string | null | undefined): string {
	return [firstName, lastName]
		.map((part) => (part ?? '').trim())
		.filter(Boolean)
		.join(' ');
}
